import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { apiRequest } from "src/async/apiUtils";
import { objectToFormData } from "src/helpers";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import StorageUploader from "src/pages/StorageUploader";
import StorageUploadProgress from "src/pages/StorageUploadProgress";
import Modal from "./Modal";
import InputGroup from "../form/Input";

const DocumentUploadManager = ({ isOpen, onClose, uploadType, parentId, refreshList }) => {
  const { workspace_id, content_type, content_value } = useParams();
  const { group, page, form, document } = useUrlQueryParams();

  const [searchParams, setSearchParams] = useSearchParams();

  const [isUploading, setIsUploading] = useState(false);
  const [documentStatusList, setDocumentStatusList] = useState([]);
  const [formData, setFormData] = useState({ name: "", documents: [] });

  const getOrCreateFolder = async (path, baseParentId, folderMap, folderNames, workspace_id) => {
    const segments = path.split("/");
    let currentParentId = baseParentId;
    let currentPath = "";

    const params = { workspace_id };
    const queries = {
      group,
      page,
      form,
      document,
      ...(content_type && content_value ? { [content_type]: content_value } : {}),
    };

    for (const segment of segments) {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      const displayName = folderNames?.[currentPath] || segment;

      if (folderMap[currentPath]) {
        currentParentId = folderMap[currentPath];
      } else {
        try {
          const res = await apiRequest(
            "post",
            `portal/workspaces/:workspace_id/documents`,
            {
              // FIXED: Removed the "-" typo and ensured proper object structure
              body: objectToFormData({
                name: displayName,
                type: "folder", // CRITICAL: This satisfies the "Type field" requirement
                parent_id: currentParentId,
              }),
              queries,
              params,
            },
            { headers: { "Content-Type": "multipart/form-data" } },
          );

          if (res.status === 200) {
            const newFolderId = res?.data?.data?._id;
            folderMap[currentPath] = newFolderId;
            currentParentId = newFolderId;
          }
        } catch (error) {
          throw new Error(`Failed to create folder: ${displayName}. ${error.message || ""}`);
        }
      }
    }
    return currentParentId;
  };

  const updateStatus = (id, status) => {
    setDocumentStatusList((prev) => prev.map((item) => (item.id === id ? { ...item, status } : item)));
  };

  const handleUpload = async () => {
    const docsToUpload = formData.documents;
    const folderMap = {};
    let baseParentId = parentId || null;

    const params = { workspace_id };
    const queries = {
      group,
      page,
      form,
      document,
      ...(content_type && content_value ? { [content_type]: content_value } : {}),
    };

    setIsUploading(true);

    // STEP 1: If "New Folder" type, create that root folder first
    if (uploadType === "new_folder") {
      if (!formData.name) {
        setIsUploading(false);
        return toast.error("Please provide a folder name");
      }

      try {
        const res = await apiRequest(
          "post",
          `portal/workspaces/:workspace_id/documents`,
          {
            body: objectToFormData({
              name: formData.name,
              type: "folder",
              parent_id: parentId || null,
            }),
            queries,
            params,
          },
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        if (res.status === 200) {
          baseParentId = res.data?.data?._id;
          if (docsToUpload.length === 0) {
            finalizeUpload();
            return;
          }
        }
      } catch (error) {
        setIsUploading(false);
        return toast.error(`Failed to create base folder: ${error.message}`);
      }
    }

    // STEP 2: Initialize UI status list
    setDocumentStatusList(
      docsToUpload.map((d) => ({
        id: d.id,
        name: d.path || d.name,
        status: "pending",
        extension: d.file.name.split(".").pop(),
      })),
    );

    // STEP 3: Process Documents
    for (let i = 0; i < docsToUpload.length; i++) {
      const doc = docsToUpload[i];
      try {
        updateStatus(doc.id, "validating");

        // Validation
        const validationFormData = new FormData();
        validationFormData.append("name", doc.name);
        validationFormData.append(`documents[0][file]`, doc.file);
        validationFormData.append(`documents[0][id]`, doc.id);
        validationFormData.append(`documents[0][name]`, doc.name);
        validationFormData.append(`documents[0][path]`, doc.path || "");

        await apiRequest("post", `portal/workspaces/:workspace_id/documents/validate`, { body: validationFormData, queries, params }, { headers: { "Content-Type": "multipart/form-data" } });

        // Folder structure resolution
        let targetFolderId = baseParentId;
        // Use webkitRelativePath for folder uploads or doc.path for manual structures
        const fullPath = doc.file.webkitRelativePath || doc.path || "";

        if (fullPath) {
          const pathSegments = fullPath.split("/");
          pathSegments.pop(); // Remove filename
          if (pathSegments.length > 0) {
            targetFolderId = await getOrCreateFolder(pathSegments.join("/"), baseParentId, folderMap, formData.folderNames || {}, workspace_id);
          }
        }

        // Final Upload
        updateStatus(doc.id, "uploading");

        const uploadFormData = new FormData();
        uploadFormData.append("name", doc.name);
        uploadFormData.append("document", doc.file);
        uploadFormData.append("type", "file"); // Explicit type for file
        if (targetFolderId) uploadFormData.append("parent_id", targetFolderId);

        await apiRequest("post", `portal/workspaces/:workspace_id/documents`, { body: uploadFormData, queries, params }, { headers: { "Content-Type": "multipart/form-data" } });

        updateStatus(doc.id, "success");
      } catch (error) {
        updateStatus(doc.id, "failed");
        if (error.message.includes("Failed to create folder")) break;
      }
    }
    finalizeUpload();
  };

  const finalizeUpload = () => {
    setIsUploading(false);
    setFormData({ name: "", documents: [] });
    searchParams.set("documentTabs", "Documents Uploaded");
    setSearchParams(searchParams);
    onClose();
    if (refreshList) refreshList();
  };

  return (
    <Modal
      title="Document"
      secondaryTitle={uploadType === "new_folder" ? "New Folder" : uploadType === "file" ? "File Upload" : "Folder Upload"}
      size="xxl"
      overlayClasses="!z-[80]"
      isOpen={isOpen}
      isLoading={isUploading}
      onCancel={onClose}
      onSuccess={handleUpload}
      defaultOptions={{ onSuccessButtonText: "Upload" }}>
      <div className="min-h-[400px] space-y-4">
        {uploadType === "new_folder" && (
          <InputGroup
            inline
            name="name"
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        )}
        {isUploading ? (
          <StorageUploadProgress
            documentStatusList={documentStatusList}
            onClose={() => {
              setIsUploading(false);
              setFormData({ name: "", documents: [] });
            }}
          />
        ) : (
          <StorageUploader
            formData={formData}
            setFormData={setFormData}
            uploadType={["new_folder", "file"].includes(uploadType) ? "file" : "folder"}
          />
        )}
      </div>
    </Modal>
  );
};

export default DocumentUploadManager;
